import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../../App';
import {
  getOcAssigneeCandidates,
  getOcWorkflowSettings,
  patchOcWorkflowSettings,
} from '../../clients/oc_request_client';
import AssigneeUserSelect from '../../components/OrganoidRequest/AssigneeUserSelect';
import OcWorkspaceShell from '../../components/OrganoidRequest/OcWorkspaceShell';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcWorkflowSettingsPage';

const DEFAULT_FIELDS = [
  {
    key: 'default_admin_id',
    label: 'Administrative review',
  },
  {
    key: 'default_data_manager_id',
    label: 'Data manager (matching)',
  },
  {
    key: 'default_technician_id',
    label: 'Technician (tech review + prep)',
  },
];

/**
 * Lab-only defaults for OC stage owners at /organoid-requests/settings.
 */
const OcWorkflowSettingsPage = () => {
  const { token } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    default_admin_id: null,
    default_data_manager_id: null,
    default_technician_id: null,
  });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const [settingsResult, candidatesResult] = await Promise.all([
      getOcWorkflowSettings(token),
      getOcAssigneeCandidates(token),
    ]);
    setLoading(false);

    if (!settingsResult.success) {
      showToast(
        settingsResult.messages?.[0] || 'Failed to load workflow settings',
        'error',
        'top-right',
        4000
      );
      return;
    }
    if (!candidatesResult.success) {
      showToast(
        candidatesResult.messages?.[0] || 'Failed to load assignee candidates',
        'error',
        'top-right',
        4000
      );
    } else {
      setCandidates(candidatesResult.data || []);
    }

    const data = settingsResult.data || {};
    setSettings(data);
    setForm({
      default_admin_id: data.default_admin_id ?? null,
      default_data_manager_id: data.default_data_manager_id ?? null,
      default_technician_id: data.default_technician_id ?? null,
    });
    logInfo(LOG_CONTEXT, 'settings loaded', {
      updated_at: data.updated_at,
    });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!token || !settings) return;
    const payload = {};
    DEFAULT_FIELDS.forEach(({ key }) => {
      const next = form[key] ?? null;
      const prev = settings[key] ?? null;
      if (next !== prev) {
        payload[key] = next;
      }
    });
    if (Object.keys(payload).length === 0) {
      showToast('No changes to save', 'info', 'top-right', 2500);
      return;
    }
    setSaving(true);
    const result = await patchOcWorkflowSettings(token, payload);
    setSaving(false);
    if (result.success) {
      setSettings(result.data);
      setForm({
        default_admin_id: result.data.default_admin_id ?? null,
        default_data_manager_id: result.data.default_data_manager_id ?? null,
        default_technician_id: result.data.default_technician_id ?? null,
      });
      logInfo(LOG_CONTEXT, 'settings saved', { keys: Object.keys(payload) });
      showToast('Workflow assignees saved', 'success', 'top-right', 3000);
    } else {
      const statusHint =
        result.status === 403
          ? ' (forbidden — lab staff only)'
          : result.status === 400
            ? ' (invalid selection)'
            : '';
      showToast(
        `${result.messages?.[0] || 'Save failed'}${statusHint}`,
        'error',
        'top-right',
        5000
      );
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const updatedBy =
    settings?.updated_by_username ||
    settings?.updated_by_detail?.username ||
    settings?.updated_by ||
    null;

  return (
    <OcWorkspaceShell theme="yellow">
      <Button as={Link} to="/organoid-requests" variant="link" className="px-0 mb-2">
        ← Organoid requests
      </Button>
      <h2 className="mb-3">Organoid workflow assignees</h2>
      <p className="text-muted">
        Default stage owners applied when a request has no per-request assignee for that
        role. Clear a field to fall back to group notification.
      </p>

      <Card className="oc-glass-panel border-0">
        <Card.Body>
          {DEFAULT_FIELDS.map(field => (
            <AssigneeUserSelect
              key={field.key}
              controlId={`oc-settings-${field.key}`}
              label={field.label}
              options={candidates}
              value={form[field.key]}
              onChange={id => setForm(prev => ({ ...prev, [field.key]: id }))}
              disabled={saving}
            />
          ))}
          <Button className="oc-primary-btn" onClick={save} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>
          {settings?.updated_at ? (
            <div className="text-muted small mt-3">
              Last updated: {new Date(settings.updated_at).toLocaleString()}
              {updatedBy ? ` by ${updatedBy}` : ''}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </OcWorkspaceShell>
  );
};

export default OcWorkflowSettingsPage;
